// Taşeron portalı — firma switcher (header). Aktif firmayı gösterir; supplier birden
// çok firmaya bağlıysa dropdown ile firma değiştirilir → `/auth/switch-company`
// (api-contract §5.6). Erişilemeyen firmada backend `403 COMPANY_NOT_ACCESSIBLE`
// döner; menü açık kalır ve mesaj gösterilir. Aktif firma auth store'da
// (`principal.companyId`); adlar erişilebilir firma listesinden gelir.
//
// **Müşteri başına bekleyen iş özeti (api-contract §28.4, plan S26 3.5).** Rozet + menüdeki
// satır başına durum `GET /supplier/workspaces/summary`'den gelir — TEK sayım kaynağı §28.1,
// burada yeniden hesaplanmaz. Sayaçlar MENÜ AÇILINCA çekilir (`enabled: open`), sayfa
// yüklenirken DEĞİL — nadiren açılan bir menü için bayat bir rozet, kısa bir iskeletten
// DAHA KÖTÜdür. `staleTime: 60_000` — özet bir KARAR YARDIMIDIR, bir KAPI değildir (§28.4).
//
// `null` (state ≠ 'ok') = BİLİNMİYOR, `0` = ENGEL YOK — ikisi ASLA aynı görünmez (§28.2).
// Rozet MÜŞTERİ sayar, KİŞİ değil; erişilebilir etiketi bunu netleştirir (plan S26 "beş
// düzeltme" #1).

import {
  Alert,
  Spinner,
  currentCulture,
  resolveApiErrorMessage,
  supplierDashboardApi,
  switchCompany,
  useAuthStore,
} from '@isgportal/shared';
import type { SupplierCompanyItem, SupplierWorkspaceSummaryDto } from '@isgportal/shared';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowRight, Building2, CheckCircle2, ChevronDown, CircleHelp } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTime } from '../lib/dashboardTime';
import { supplierDashboardKeys } from '../lib/supplierDashboardKeys';
import { useSupplierMe } from '../lib/useSupplierMe';

// ============================================================================
// Satır durumu — özet henüz gelmediyse/gelemediyse "bilinmiyor"dur, ASLA "temiz" değil.
// ============================================================================

type RowStatus =
  | { kind: 'unknown' }
  | { kind: 'unauthorized' }
  | { kind: 'unavailable' }
  | {
      kind: 'ok';
      blockedToday: number;
      atRiskThisWeek: number;
      nextCutoffAt: string | null;
      /** §28.4 — ÇAĞIRANIN zon'u (firmanın değil), satırdan okunur (§28 girişi ile aynı zon). */
      timezone: string;
    };

interface SwitcherRow {
  companyId: number;
  companyName: string;
  status: RowStatus;
}

// Bulgu #5 (S26 tasarım incelemesi) — `row === undefined` İKİ FARKLI durumu kapsıyordu: yanıt
// henüz gelmedi (`unknown`, gerçekten yükleniyor) VE yanıt geldi ama bu satır İÇİNDE değildi
// (§28.4 fan-out tavanı `MaxWorkspaceFanout`, varsayılan 25 — 26+ müşterili bir supplier'da
// 25'in ötesindeki satırlar hiç dönmez). `hasData` bu ikisini ayırır: yanıt geldiyse eksik
// satır SONSUZA DEK nabız atan bir iskelet DEĞİL, `unavailable`'dır.
function statusFor(row: SupplierWorkspaceSummaryDto | undefined, hasData: boolean): RowStatus {
  if (!row) {
    return hasData ? { kind: 'unavailable' } : { kind: 'unknown' };
  }
  if (row.state === 'ok') {
    return {
      kind: 'ok',
      blockedToday: row.blockedToday,
      atRiskThisWeek: row.atRiskThisWeek,
      nextCutoffAt: row.nextCutoffAt,
      timezone: row.timezone,
    };
  }
  return { kind: row.state };
}

/** Aciliyet sırası — 0 en acil. `blockedToday` > `atRiskThisWeek` > bilinmiyor > temiz. */
function urgencyRank(status: RowStatus): 0 | 1 | 2 | 3 {
  if (status.kind === 'ok') {
    if (status.blockedToday > 0) return 0;
    if (status.atRiskThisWeek > 0) return 1;
    return 3;
  }
  // `unknown` (özet henüz yok) ve `unauthorized`/`unavailable` (çözülemedi) aynı aciliyet
  // bandında — ikisi de "temiz" DEĞİLDİR ve "temiz"in ÜSTÜNDE gösterilir (plan S26 tuzak:
  // bilinmeyeni yolunda göstermek en kötü hata modudur).
  return 2;
}

function compareRows(a: SwitcherRow, b: SwitcherRow): number {
  const ra = urgencyRank(a.status);
  const rb = urgencyRank(b.status);
  if (ra !== rb) {
    return ra - rb;
  }
  if (ra === 0 && a.status.kind === 'ok' && b.status.kind === 'ok') {
    return b.status.blockedToday - a.status.blockedToday;
  }
  if (ra === 1 && a.status.kind === 'ok' && b.status.kind === 'ok') {
    return b.status.atRiskThisWeek - a.status.atRiskThisWeek;
  }
  // Aciliyet eşitse (aynı bantta) firma adı yalnız EŞİTLİK BOZUCUDUR — birincil anahtar
  // ASLA alfabetik değildir (plan S26 3.5 #3).
  return a.companyName.localeCompare(b.companyName, 'tr');
}

function buildRows(
  companies: SupplierCompanyItem[],
  summary: SupplierWorkspaceSummaryDto[] | undefined,
): SwitcherRow[] {
  const byId = new Map(summary?.map((row) => [row.companyId, row]));
  const hasData = summary !== undefined;
  return companies.map((c) => ({
    companyId: c.id,
    companyName: c.name || `#${c.id}`,
    status: statusFor(byId.get(c.id), hasData),
  }));
}

type TFunction = (key: string, options?: Record<string, unknown>) => string;

/**
 * Satır durum satırı — renk TEK BAŞINA anlam taşımaz, her dal kendi metnini + ikonunu taşır.
 *
 * S26 tasarım incelemesi (A-1) — sayı çeviri dizesinden ÇIKTI: nokta (dekoratif, aria-hidden)
 * + rakam (`tabular-nums`, ayrı tipografik nesne) + STATİK durum sözcüğü (çoğul eki yok,
 * sayıyla değişmez). `Badge` bu satırdan tamamen düşer — 25 satırlık bir menüde dolu hap
 * sinyali yok ederdi (SAP: "inverted … not more than two on one page") ve D4'ün kaynağı olan
 * kırpma/sarma sorunu bir hap OLMADIĞI için burada yeniden ortaya çıkmaz. Üç görünür parça
 * (nokta + rakam + sözcük) `aria-label` ile TEK cümle olarak da sunulur — ekran okuyucu
 * parçaları tek tek okumak yerine "4 Bugün engellenen" bütününü duyar.
 */
function RowStatusLine({
  status,
  culture,
  t,
}: {
  status: RowStatus;
  culture: string;
  t: TFunction;
}) {
  if (status.kind === 'unknown') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className="inline-block h-3 w-20 animate-pulse rounded bg-surface-muted"
        />
        <span className="sr-only">{t('switcher.summary.loading')}</span>
      </span>
    );
  }
  if (status.kind === 'unauthorized') {
    return (
      <span className="inline-flex items-center gap-1 text-muted">
        <CircleHelp className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {t('switcher.noAccess')}
      </span>
    );
  }
  if (status.kind === 'unavailable') {
    return (
      <span className="inline-flex items-center gap-1 text-muted">
        <CircleHelp className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {t('switcher.row.unavailable')}
      </span>
    );
  }
  if (status.blockedToday > 0) {
    const label = t('switcher.state.blocked');
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <span
          className="inline-flex items-center gap-1.5"
          aria-label={`${status.blockedToday} ${label}`}
        >
          <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-danger" />
          <span aria-hidden="true" className="tabular-nums font-semibold text-fg">
            {status.blockedToday}
          </span>
          <span aria-hidden="true" className="text-muted">
            {label}
          </span>
        </span>
        {status.nextCutoffAt ? (
          // S26 — "son yükleme" etiket sözcüğü SİLİNDİ, saat kalıyor (D4: Almanca en uzun
          // dize artık `switcher.state.atRisk` üzerinden ~19 karakter, önceki 46'nın çok
          // altında). §28.4 normatif not korunur: CİHAZ zon'unda BİÇİMLENDİRİLMEZ — satırın
          // kendi `timezone`'u kullanılır.
          <span className="tabular-nums text-muted">
            · {formatTime(status.nextCutoffAt, status.timezone, culture)}
          </span>
        ) : null}
      </span>
    );
  }
  if (status.atRiskThisWeek > 0) {
    const label = t('switcher.state.atRisk');
    return (
      <span
        className="inline-flex items-center gap-1.5"
        aria-label={`${status.atRiskThisWeek} ${label}`}
      >
        <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-warning" />
        <span aria-hidden="true" className="tabular-nums font-semibold text-fg">
          {status.atRiskThisWeek}
        </span>
        <span aria-hidden="true" className="text-muted">
          {label}
        </span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-muted">
      <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden="true" />
      {t('switcher.row.clean')}
    </span>
  );
}

export function CompanySwitcher() {
  const { t } = useTranslation();
  const culture = currentCulture();
  const principal = useAuthStore((s) => s.principal);
  const companies = useAuthStore((s) => s.companies);

  const activeId = principal?.companyId ?? null;
  const active = companies.find((c) => c.id === activeId);
  // S37-1 (S1): ad zinciri `companies[].name` → `me.companyName` → `#id` (auth store'daki
  // liste ad taşımayabilir; self-profil token'daki aktif firmanın adını verir).
  const { me } = useSupplierMe();
  const activeLabel =
    active?.name || me?.companyName?.trim() || (activeId != null ? `#${activeId}` : '—');
  const isMultiCompany = companies.length > 1;

  const [open, setOpen] = useState(false);

  // §28.4 — menü AÇILINCA çekilir (`enabled: open`), sayfa yüklenirken DEĞİL. Query anahtarı
  // aktif `companyId`'yi taşır (plan S26 3.5 #4 notu + supplierDashboardKeys) — yanıt çok
  // firmayı kapsasa da, aktif firma değişince taşeron bağı yeniden çözülür ve tüm satırlar
  // geçersiz olur.
  const summaryQuery = useQuery({
    queryKey: supplierDashboardKeys.workspacesSummary(activeId),
    queryFn: () => supplierDashboardApi.getSupplierWorkspacesSummary(),
    enabled: open && isMultiCompany,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: (companyId: number) => switchCompany(companyId),
    // BUG FIX (HIGH, live QA — 3. deneme). Önceki iki deneme (bkz. git geçmişi)
    // TanStack cache'ini sırayla (`cancelQueries` → `clear` → `navigate`) yönetmeye
    // çalıştı; her ikisi de hızlı ardışık firma değişiminde bir yarış BIRAKTI, çünkü
    // `AppLayout`'taki `useSupplierContractorAccess` firma geçişinde HİÇ unmount
    // OLMAZ (yalnız sayfa `navigate('/')` ile yeniden mount olur) — sayfa seviyesi
    // query'leri kurtaran remount'ın burada karşılığı yok, o yüzden herhangi bir
    // ara sıralama hatası bu query'yi kalıcı olarak "fetching"te bırakıp sidebar'ı
    // sonsuza dek yükleniyor gösterebiliyordu.
    //
    // Çözüm: sıralamayı DOĞRU yapmaya çalışmak yerine TAM SAYFA RELOAD — TanStack
    // cache'i (ve içindeki her gözlemci) sıfırdan kurulur, hiçbir zombi query
    // mümkün DEĞİLDİR (şimdi ya da ileride eklenecek herhangi bir kalıcı query
    // için). Firma değişimi nadir, kasıtlı, açıkça onaylanan bir eylemdir ve
    // `/auth/switch-company` zaten yepyeni bir terminal token üretir — reload'un
    // ~1 sn'lik maliyeti, üçüncü bir yarış denemesinden daha iyi bir takas.
    onSuccess: () => {
      // Menüyü kapat — reload'a kadar `mutation.isPending` true kalır (satırdaki
      // Spinner görünür kalır), kullanıcı "ölü" bir arayüz yerine geçişin sürdüğünü
      // görür.
      setOpen(false);
      // Panele döner, MEVCUT URL'E DEĞİL — `/permits/:uuid` gibi eski firmanın
      // kimliğini taşıyan bir derin rotadaysak o kimlik yeni firmada YOKTUR
      // (404/403). Token yazımı burada BEKLENMEZ: `switchCompany()` (auth/supplier.ts)
      // yeni terminal token'ın persist yazımını (idb) zaten `await` ederek döner —
      // bu callback'e gelindiğinde yazım BİTMİŞTİR.
      window.location.assign('/');
    },
  });

  // Tek firma → salt gösterim (değiştirilecek başka firma yok); özet ÇEKİLMEZ, menü YOK.
  if (!isMultiCompany) {
    return (
      <div className="inline-flex h-12 items-center gap-2 px-0 text-sm text-shell-fg-muted">
        <span className="sr-only">{t('switcher.label')}: </span>
        {/* Bulgu #8 (S26 tasarım incelemesi) — 640px altında bu etiket tamamen GİZLİYDİ; o
            genişlikte ekranda aktif müşteriyi ADLANDIRAN tek yer switcher'dı, ve o da
            gizliydi. Artık her genişlikte görünür, yalnız genişliği kısılır. */}
        <span className="max-w-[12rem] truncate sm:max-w-[24rem]">{activeLabel}</span>
      </div>
    );
  }

  const rows = buildRows(companies, summaryQuery.data?.items);
  // Bulgu #5 — §28.4 fan-out tavanı (`MaxWorkspaceFanout`, varsayılan 25): yanıt
  // `total` GERÇEK erişilebilir firma sayısını bildirir, `items.length < total` ise tavan
  // aşılmış demektir. Menü altbilgisine eklenir ("25 / 40 firma özetlendi").
  const fanoutCapped = summaryQuery.data
    ? summaryQuery.data.items.length < summaryQuery.data.total
    : false;
  const activeRow = rows.find((r) => r.companyId === activeId);
  const otherRows = rows.filter((r) => r.companyId !== activeId);
  // Aciliyete göre sıralama YALNIZ özet başarıyla geldiğinde uygulanır — yükleniyor/hata
  // durumunda anlamsız bir sıralama YAPILMAZ, doğal (auth store) sıra korunur.
  const sortedOtherRows = summaryQuery.data ? [...otherRows].sort(compareRows) : otherRows;

  // Rozet MÜŞTERİ sayısıdır (KİŞİ değil) — özet gelmediyse rozet OMİTİLİR, `0` gibi
  // gösterilmez (plan S26 tuzak: bilinmeyeni "yolunda" göstermek en kötü hata modu).
  const attentionCount = summaryQuery.data
    ? otherRows.filter((r) => urgencyRank(r.status) <= 1).length
    : null;

  const triggerLabel =
    attentionCount && attentionCount > 0
      ? `${t('switcher.change')} — ${t('switcher.badge.ariaLabel', { count: attentionCount })}`
      : t('switcher.change');

  return (
    <DropdownMenu.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          mutation.reset();
        }
      }}
    >
      <DropdownMenu.Trigger
        className="inline-flex h-12 items-center gap-2 px-2 text-sm text-shell-fg-muted hover:bg-shell-hover hover:text-shell-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-shell-fg"
        aria-label={triggerLabel}
      >
        {/* Bulgu #8 — mobilde aktif müşteriyi adlandıran tek yer; her genişlikte görünür. */}
        <span className="max-w-[12rem] truncate sm:max-w-[24rem]">{activeLabel}</span>
        {attentionCount && attentionCount > 0 ? (
          <span
            aria-hidden="true"
            className="inline-flex h-4 min-w-[1rem] shrink-0 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold tabular-nums text-danger-foreground"
          >
            {attentionCount}
          </span>
        ) : null}
        <ChevronDown className="h-4 w-4" aria-hidden="true" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={0}
          className="z-[var(--z-dropdown)] min-w-[18rem] max-w-[26rem] overflow-hidden rounded-md border border-border bg-surface p-1 shadow-md"
        >
          <div className="flex items-center justify-between gap-2 px-2 py-1.5">
            <span className="text-xs font-semibold text-muted">{t('switcher.menu.heading')}</span>
            {summaryQuery.fetchStatus === 'fetching' ? (
              <Spinner label={t('switcher.summary.loading')} />
            ) : null}
          </div>

          {mutation.isError ? (
            <div className="px-1 pb-1">
              <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
            </div>
          ) : null}

          {summaryQuery.isError && !summaryQuery.data ? (
            <div className="flex flex-col gap-1.5 px-1 pb-1">
              <Alert tone="warning">{resolveApiErrorMessage(summaryQuery.error, t)}</Alert>
              <button
                type="button"
                onClick={() => summaryQuery.refetch()}
                className="self-start rounded px-1 text-xs font-medium text-primary hover:underline"
              >
                {t('state.error.retry')}
              </button>
            </div>
          ) : null}

          <DropdownMenu.Separator className="my-1 h-px bg-border" />

          {activeRow ? (
            <div
              aria-current="true"
              className="flex items-start gap-2 border-l-2 border-l-primary bg-primary/5 px-2 py-2"
            >
              <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <div className="flex min-w-0 flex-col gap-1">
                <span className="truncate text-sm font-semibold text-fg">
                  {activeRow.companyName}
                </span>
                <span className="flex flex-wrap items-center gap-1.5 text-xs">
                  <span className="text-muted">{t('switcher.activeNote')}</span>
                  <span className="text-muted" aria-hidden="true">
                    ·
                  </span>
                  <RowStatusLine status={activeRow.status} culture={culture} t={t} />
                </span>
              </div>
            </div>
          ) : null}

          <DropdownMenu.Separator className="my-1 h-px bg-border" />

          <div className="max-h-[28rem] overflow-y-auto overscroll-contain">
            {sortedOtherRows.map((row) => {
              const isSwitchingThis = mutation.isPending && mutation.variables === row.companyId;
              return (
                <DropdownMenu.Item
                  key={row.companyId}
                  disabled={mutation.isPending}
                  onSelect={(e) => {
                    // Menüyü açık tut — switch sonucu (başarı/403) burada görünsün.
                    e.preventDefault();
                    if (!mutation.isPending) {
                      mutation.mutate(row.companyId);
                    }
                  }}
                  className="flex cursor-pointer select-none items-start gap-2 rounded px-2 py-2 text-sm text-fg outline-none data-[disabled]:cursor-default data-[highlighted]:bg-surface-muted data-[disabled]:opacity-100"
                >
                  <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="truncate font-medium text-fg">{row.companyName}</span>
                    <span className="text-xs">
                      <RowStatusLine status={row.status} culture={culture} t={t} />
                    </span>
                  </div>
                  {isSwitchingThis ? (
                    <Spinner label={t('switcher.switching')} />
                  ) : (
                    <span className="flex shrink-0 items-center gap-1 self-center text-xs font-medium text-primary">
                      {t('switcher.row.goToPanel')}
                      <ArrowRight className="h-3 w-3" aria-hidden="true" />
                    </span>
                  )}
                </DropdownMenu.Item>
              );
            })}
          </div>

          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          {fanoutCapped && summaryQuery.data ? (
            <p className="px-2 pt-1.5 text-[11px] text-muted">
              {t('switcher.menu.fanoutCap', {
                shown: summaryQuery.data.items.length,
                total: summaryQuery.data.total,
              })}
            </p>
          ) : null}
          <p className="px-2 py-1.5 text-[11px] text-muted">{t('switcher.menu.footer')}</p>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
